/**
 * Button.js
 *
 * The foundational action control of the Lantern design system (ChoreStory).
 * One component drives every call-to-action across all 18 surfaces — it reads
 * the surrounding register + theme tokens (`--accent`, `--on-accent`,
 * `--accent-text`, `--text-mid`, …) so a single `<Button>` is gold-on-ink in
 * the dark Hearth, deep-amber on parchment in light mode, and river-blue in the
 * parent Ledger — no per-surface variants in the consumer. Mirrors the
 * mockups' `.l-btn` family (design-brief §5.5).
 *
 * Anatomy:
 *   [leading icon]  Label  [trailing icon]            ← the pill (button | link)
 *   ⚠ Couldn't save — try again.                       ← optional inline error
 *
 * Variants (design-brief §5.5 / component-inventory#Button):
 *   - primary    accent fill / on-accent text (the default CTA)
 *   - secondary  transparent, accent outline + accent-text
 *   - quiet      text-only (no fill, no border)
 *   - ceremony   oversized celebratory CTA — fixed lantern gold gradient + glow
 *                (the "settle the week" / "light it up" moment; reward gold is
 *                pinned to the lantern ramp, NOT the theme-flipping --accent)
 *   - danger     destructive — calm rose outline (Ledger surfaces only)
 * `sm` is a SIZE, orthogonal to the visual variant (the mockups ship
 * `l-btn sm secondary`, `l-btn sm quiet`, …), exposed via `size="sm"`. Passing
 * the legacy `variant="sm"` is accepted and mapped to `size="sm"` + primary.
 *
 * States: default, hover, focus-visible, active, disabled, plus the two states
 * the per-screen specs flag as MISSING for this component — `loading` (leading
 * icon swaps to a spinner, the control goes busy + non-interactive, announced
 * via aria-busy + a polite live region) and `error` (a rose inline message
 * below, Ledger error semantics per brief §2.7 / §5.4 — kid Hearth surfaces use
 * the lantern-dim setback pattern instead and should not pass `error`).
 *
 * Element: renders an <a> when `href` is supplied (with safe rel defaults for
 * external targets), otherwise a <button>. A disabled/loading link degrades to
 * an inert `aria-disabled` link (no href, removed from the tab order) since
 * native anchors can't be disabled.
 *
 * Self-contained per the implement plan (one file per component, no sibling
 * .css): styles ship once via useInsertionEffect under a namespaced `csb-*`
 * class set, every value `var(--token, fallback)` so it also renders calmly
 * standalone.
 *
 * Attribute contract (CR-04) — the primitive every action control mirrors:
 *   - NAME: an explicit caller `aria-label` / `aria-labelledby` / `title` is
 *     forwarded untouched. The generic "Button" fallback name applies ONLY when
 *     the control has no children AND no explicit caller name.
 *   - DESCRIPTION: `aria-describedby` COMPOSES — the caller's ids come first,
 *     then the component's own error id while an error shows. It is never
 *     replaced, and it is never emitted as an empty attribute.
 *   - IDENTITY: `data-*`, `className`, `aria-busy`, disabled/inert wiring and
 *     the click guard are component-owned and win over `...rest`.
 */

import React, { useEffect, useId, useInsertionEffect, useRef } from 'react';
const safeRest = (o) => { if (!o || typeof o !== "object") return o; const clean = (h) => (typeof h === "string" && /^(javascript|vbscript|data):/.test(h.trim().toLowerCase().replace(/[\s\u0000-\u001f]+/g, "")) ? undefined : h); const { dangerouslySetInnerHTML, srcDoc, srcdoc, ...r } = o; for (const k of ["href", "src", "action", "formAction", "poster"]) if (k in r) r[k] = clean(r[k]); return r; };

/* ------------------------------------------------------------------ *
 * Variant / size vocabulary                                           *
 * ------------------------------------------------------------------ */

const VARIANTS = new Set(['primary', 'secondary', 'quiet', 'ceremony', 'danger']);
const SIZES = new Set(['sm', 'md']);

/* ------------------------------------------------------------------ *
 * Dev-only guards (never throw, fire at most once per message)        *
 * ------------------------------------------------------------------ */

const warnedKeys = new Set();

/**
 * Dev-only, never-throwing warning. Fires under Node/test/bundlers that define
 * `process.env.NODE_ENV`; stays silent in production and in browser bundles
 * without a `process` global.
 */
function devWarn(key, message) {
  if (warnedKeys.has(key)) return;
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV !== 'production' &&
      typeof console !== 'undefined'
    ) {
      warnedKeys.add(key);
      console.warn(message);
    }
  } catch (_) {
    /* no-op: warnings must never break rendering */
  }
}

/**
 * Resolve a user-facing error string from a string | Error | error-like object,
 * falling back to `fallback` when nothing meaningful is present. Guards against
 * an empty `new Error('')` being silently swallowed and a plain object
 * stringifying to "[object Object]".
 */
function resolveErrorMessage(error, fallback) {
  if (error == null || error === false) return null;
  if (error === true) return fallback;
  let raw;
  if (typeof error === 'string') {
    raw = error;
  } else if (error instanceof Error) {
    raw = error.message;
  } else if (typeof error === 'object') {
    raw = error.message != null ? String(error.message) : '';
  } else {
    raw = String(error);
  }
  return raw.trim() || fallback;
}

/* ------------------------------------------------------------------ *
 * Scoped stylesheet (injected once)                                   *
 * ------------------------------------------------------------------ */

const STYLE_ID = 'csb-styles';
const STYLES = `
.csb {
  /* Transparent wrapper: vanishes from layout so a bare <Button> behaves like a
     bare button in its parent (flex row, grid, inline text). It only becomes a
     real box when an inline error is shown, so toggling error never remounts —
     and never steals — the focused control. */
  display: contents;
}
.csb--haserror {
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--s-2, 8px);
}
.csb--block, .csb--block.csb--haserror { display: flex; }

.csb__btn {
  font: 600 var(--fs-sm, .9rem) var(--font-ui, "Inter", system-ui, -apple-system, "Segoe UI", sans-serif);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s-2, 8px);
  min-height: 44px;
  padding: 0 var(--s-6, 24px);
  border-radius: 999px;
  border: 1.5px solid transparent;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  background: var(--accent, #fbbf24);
  color: var(--on-accent, #0b1023);
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              box-shadow var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              background var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              border-color var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              color var(--motion-quick, 150ms) var(--ease-out-soft, ease);
}
.csb__btn--block { width: 100%; }

/* The interactive feedback applies only to a live control. We disable it for
   native :disabled, the inert-link [aria-disabled], and the busy state. */
.csb__btn:hover:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
  transform: translateY(-1px);
}
.csb__btn:active:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
  transform: translateY(0);
}
.csb__btn:focus-visible {
  /* Two-tone focus ring. The inner surface-coloured band guarantees the ring is
     delineated from the fill even when --accent IS the fill (primary / ceremony),
     so the indicator never collapses into the button itself (WCAG 2.4.7 / 1.4.11)
     regardless of the page background. The transparent outline is the
     forced-colors-mode fallback (box-shadow is dropped there; a transparent
     outline is repainted by the OS as a visible system focus ring). */
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--surface-1, #0b1023),
              0 0 0 4px var(--accent, #fbbf24);
}

/* ---- primary: accent fill (default; styling inherited from .csb__btn) ----
   No hover glow: the brief makes the Ledger hover glow optional ("may"), and a
   box-shadow glow here would override the box-shadow focus ring on hover+focus.
   The translateY lift from the base :hover rule is the hover affordance. */

/* ---- secondary: accent outline ---- */
.csb__btn--secondary {
  background: transparent;
  color: var(--accent-text, #2c7a9e);
  border-color: var(--accent, #fbbf24);
}
.csb__btn--secondary:hover:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
  background: color-mix(in oklab, var(--accent, #fbbf24) 12%, transparent);
}

/* ---- quiet: text only ---- */
.csb__btn--quiet {
  background: transparent;
  color: var(--text-mid, #424a66);
}
.csb__btn--quiet:hover:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
  background: color-mix(in oklab, var(--text-mid, #424a66) 12%, transparent);
  transform: none;
}

/* ---- ceremony: oversized celebratory CTA (fixed lantern gold + glow) ---- */
.csb__btn--ceremony {
  min-height: 56px;
  padding: 0 var(--s-8, 32px);
  font: 700 var(--fs-base, 1rem) var(--font-ui, "Inter", system-ui, sans-serif);
  font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg, var(--lantern-300, #fcd34d), var(--lantern-400, #fbbf24));
  color: var(--ink-900, #0b1023);
  border-color: transparent;
  box-shadow: var(--shadow-glow, 0 0 24px rgba(251,191,36,.45));
}
/* Light entry surfaces (always-parchment login/signup/onboarding/auth) ship the
   ceremony CTA as a solid deep-amber fill on parchment (mockups .cta-finish,
   .warm), not the dark gradient. Gold is pinned to the lantern ramp — never the
   register-flipping --accent, which would go river in the parent Ledger. */
[data-theme="light"] .csb__btn--ceremony {
  background: var(--lantern-700, #a87900);
  color: var(--parchment-50, #faf6ee);
}

/* ---- danger: calm rose outline (Ledger only) ---- */
.csb__btn--danger {
  background: transparent;
  color: oklch(0.67 0.19 20); /* #280: lighter rose text, AA on dark surface (was --rose-500 0.62 = 4.35:1). Token stays for border/hover/focus + any solid fill. NOTE: dark-optimized; light theme (best-effort) would want a darker rose — proper fix is a dedicated --rose-text token. */
  border-color: color-mix(in oklab, var(--rose-500, #c43d33) 60%, transparent);
}
.csb__btn--danger:hover:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
  background: color-mix(in oklab, var(--rose-500, #c43d33) 14%, transparent);
  border-color: var(--rose-500, #c43d33);
}
.csb__btn--danger:focus-visible {
  box-shadow: 0 0 0 2px var(--surface-1, #0b1023),
              0 0 0 4px var(--rose-500, #c43d33);
}

/* ---- size: sm (compact, dense Ledger UIs) ---- */
.csb__btn--sm {
  min-height: 32px;
  padding: 0 var(--s-4, 16px);
  font-size: var(--fs-xs, .75rem);
  gap: 6px;
}

/* Ledger register: standard controls relax to 36px (brief §5.5). sm and
   ceremony keep their intrinsic heights. */
[data-register="ledger"] .csb__btn:not(.csb__btn--sm):not(.csb__btn--ceremony) {
  min-height: 36px;
}

/* ---- disabled / busy ---- */
.csb__btn:disabled,
.csb__btn[aria-disabled="true"] {
  cursor: not-allowed;
  opacity: .5;
  box-shadow: none;
  transform: none;
}
.csb__btn[aria-busy="true"] {
  cursor: progress;
  opacity: .85;
}

/* ---- icons & spinner ---- */
.csb__icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  line-height: 0;
}
.csb__spinner {
  flex: none;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 2px solid color-mix(in oklab, currentColor 28%, transparent);
  border-top-color: currentColor;
  /* visible as a partial ring even when motion is disabled */
}
.csb__label { min-width: 0; }

/* ---- inline error (rose, Ledger error semantics) ---- */
.csb__error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  color: var(--rose-500, #c43d33);
  font: 500 var(--fs-sm, .9rem) var(--font-ui, "Inter", system-ui, sans-serif);
  line-height: 1.35;
}
.csb__error svg { flex: none; margin-top: 2px; }

/* ---- visually-hidden (live region) ---- */
.csb__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .csb__spinner { animation: csb-spin .7s linear infinite; }
  @keyframes csb-spin { to { transform: rotate(360deg); } }
}

/* Respect reduced motion everywhere (brief §2.4): drop the transition and the
   hover/active lift so the only feedback is the instant colour/shadow change.
   The spinner is already static here (its animation is gated above). */
@media (prefers-reduced-motion: reduce) {
  .csb__btn { transition: none; }
  .csb__btn:hover:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]),
  .csb__btn:active:not(:disabled):not([aria-disabled="true"]):not([aria-busy="true"]) {
    transform: none;
  }
}

/* AD-08 touch-target floor (tokens.json targets.minimumCssPx = 44). Below the rail breakpoint
   every control here meets 44 CSS px; desktop geometry is unchanged. Measured in Chromium at 375
   before and after: docs/reviews/p05-p10/jobs/p05-23-fable-02/evidence. */
@media (max-width: 1023.98px) {
  /* the Ledger 36px relaxation and the sm size both sit under the floor on a phone */
  .csb__btn--sm,
  [data-register="ledger"] .csb__btn:not(.csb__btn--sm):not(.csb__btn--ceremony) {
    min-height: var(--target-min, 44px);
  }
}

/* AD-08 Cause C (#/signup at 320): a block button cannot break its label, so a long label such as
   "Continue with Google instead" (287px minimum) forced its card past the column. Below 375 a block
   button wraps instead of overflowing; at 375 and wider every current label fits on one line, so
   nothing changes there. */
@media (max-width: 374.98px) {
  .csb__btn--block { white-space: normal; }
}
`;

/** Inject the component's scoped stylesheet once per document. */
function useButtonStyles() {
  useInsertionEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(STYLE_ID)) return undefined;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
    return undefined;
  }, []);
}

/** Small warning glyph for the inline error row (rose, Ledger error semantics). */
function ErrorGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.5v.01" />
    </svg>
  );
}

/**
 * Button — the Lantern action primitive.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children]  Visible label. Omit only for an
 *        icon-only button, in which case supply `aria-label` (dev-warns otherwise).
 * @param {'primary'|'secondary'|'quiet'|'ceremony'|'danger'} [props.variant]
 *        Visual treatment. Default 'primary'. Passing the legacy `'sm'` is
 *        accepted and remapped to `size="sm"` + primary.
 * @param {'sm'|'md'} [props.size]  Control size. Default 'md' (44px Hearth /
 *        36px Ledger). 'sm' is the compact 32px control for dense PARENT Ledger
 *        UIs only — do NOT use it on kid Hearth or kiosk surfaces, where the
 *        brief (§2.6) mandates a ≥44px target. The component is presentational
 *        and cannot detect its surface, so this is the host's responsibility.
 * @param {string} [props.href]  When set, renders an <a> instead of a <button>.
 * @param {string} [props.target]  Anchor target (link mode). Auto-adds a safe
 *        `rel` for `target="_blank"` unless `rel` is given.
 * @param {string} [props.rel]  Anchor rel (link mode).
 * @param {'button'|'submit'|'reset'} [props.type]  Button type (button mode).
 *        Default 'button' — never an accidental form submit.
 * @param {boolean} [props.loading]  Busy state: leading icon swaps to a spinner,
 *        the control is non-interactive and announces `aria-busy` + a polite cue.
 * @param {string} [props.loadingLabel]  Optional visible label shown while
 *        loading (e.g. "Saving…"). Defaults to keeping the normal label so the
 *        control doesn't resize; a screen-reader cue is announced instead.
 * @param {string} [props.busyLabel]  Screen-reader-only busy announcement used
 *        while loading WHEN no visible `loadingLabel` is given. Default "Loading…".
 * @param {string|Error|object|boolean|null} [props.error]  Inline error message
 *        (rose, below the control). Accepts a string, Error, error-like object,
 *        or `true` (uses `errorFallback`). Ledger surfaces only — kid Hearth
 *        surfaces use the lantern-dim setback pattern, not rose error semantics.
 * @param {string} [props.errorFallback]  Message shown when `error` is truthy
 *        but carries no usable text. Default "Something went wrong — try again."
 * @param {boolean} [props.disabled]  Standard disabled state. For links this
 *        degrades to an inert `aria-disabled` anchor (no href, untabbable).
 * @param {boolean} [props.fullWidth]  Stretch to the container width. Default false.
 * @param {React.ReactNode} [props.iconLeading]  Decorative leading glyph
 *        (replaced by the spinner while loading). Mark host icons aria-hidden.
 * @param {React.ReactNode} [props.iconTrailing]  Decorative trailing glyph.
 * @param {(event) => void} [props.onClick]  Activation handler. Suppressed (and
 *        the default prevented) while disabled/loading.
 * @param {string} [props.className]        Extra classes for the wrapper.
 * @param {string} [props.buttonClassName]  Extra classes for the inner control.
 * @param {React.Ref} [props.innerRef]      Ref to the underlying button/anchor.
 * @param {Object} [props.rest]  Remaining props are forwarded to the control
 *        (e.g. `name`, `form`, `aria-*`, `data-*`, `id`). Spread first so the
 *        component's controlled props (type/href, disabled, a11y wiring) win.
 *
 * The rendered control carries `data-component="Button"`, `data-variant`,
 * `data-size` and a runtime `data-state` ("loading" | "disabled" | "error" |
 * "default") as stable hooks for tests and host-level styling.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  target,
  rel,
  type = 'button',
  loading = false,
  loadingLabel,
  busyLabel = 'Loading…',
  error = null,
  errorFallback = 'Something went wrong — try again.',
  disabled = false,
  fullWidth = false,
  iconLeading,
  iconTrailing,
  onClick,
  className = '',
  buttonClassName = '',
  innerRef,
  ...rest
}) {
  useButtonStyles();
  const errorId = useId();
  const localRef = useRef(null);
  const wasLoading = useRef(false);

  // Normalize the variant/size vocabulary. `variant="sm"` is a legacy alias for
  // the compact size; unknown variants fall back to primary (dev-warned).
  let resolvedVariant = variant;
  let resolvedSize = size;
  if (variant === 'sm') {
    resolvedVariant = 'primary';
    resolvedSize = 'sm';
  } else if (!VARIANTS.has(variant)) {
    devWarn(
      `csb-variant-${variant}`,
      `Button: unknown variant "${variant}" — falling back to "primary". ` +
        `Expected one of: primary, secondary, quiet, ceremony, danger.`,
    );
    resolvedVariant = 'primary';
  }
  if (!SIZES.has(resolvedSize)) {
    resolvedSize = 'md';
  }

  const errorMessage = resolveErrorMessage(error, errorFallback);
  const showError = !!errorMessage && !loading;
  const isLink = href != null && href !== '';
  const isBusy = !!loading;
  // A link cannot be natively disabled; an inert link is the degraded form.
  const isInert = disabled || isBusy;

  // Icon-only buttons must carry an accessible name. This is a best-effort
  // dev-only HINT, not a guarantee: we can only inspect explicit
  // aria-label / aria-labelledby / title on the props — we cannot compute the
  // accessible name of `children` (an icon passed as children may render no text
  // yet won't trip this, and a visually-hidden text child satisfies the name
  // without an aria-label). It catches the most common mistake: no children and
  // no explicit name.
  const missingName = children == null && !rest['aria-label'] && !rest['aria-labelledby'] && !rest.title;
  if (missingName) {
    devWarn(
      'csb-icononly-noname',
      'Button: rendered without visible children — provide an `aria-label` ' +
        '(or aria-labelledby/title) so the control has an accessible name.',
    );
  }

  // While loading, a <button> is natively `disabled` (and a link is inert), so
  // it drops out of the tab order and the browser moves focus to <body>. When
  // the busy cycle ends — whether it succeeds or fails — return focus to the
  // re-enabled control so keyboard / AT users aren't stranded. Guarded twice so
  // we never STEAL focus: only when focus was actually lost (now on <body>/null)
  // and the control is interactive again (not still disabled).
  useEffect(() => {
    if (wasLoading.current && !isBusy && !isInert && localRef.current) {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (active == null || active === document.body) {
        try {
          localRef.current.focus();
        } catch (_) {
          /* focus is best-effort */
        }
      }
    }
    wasLoading.current = isBusy;
  }, [isBusy, isInert]);

  const setRef = (node) => {
    localRef.current = node;
    if (typeof innerRef === 'function') innerRef(node);
    else if (innerRef && typeof innerRef === 'object') innerRef.current = node;
  };

  const handleClick = (event) => {
    if (isInert) {
      event.preventDefault();
      return;
    }
    if (onClick) onClick(event);
  };

  const visibleLabel = isBusy && loadingLabel != null ? loadingLabel : children;

  const wrapperClass = ['csb', showError ? 'csb--haserror' : '', fullWidth ? 'csb--block' : '', className]
    .filter(Boolean)
    .join(' ');

  const btnClass = [
    'csb__btn',
    `csb__btn--${resolvedVariant}`,
    resolvedSize === 'sm' ? 'csb__btn--sm' : '',
    fullWidth ? 'csb__btn--block' : '',
    buttonClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const dataState = isBusy ? 'loading' : disabled ? 'disabled' : showError ? 'error' : 'default';

  // Shared content: spinner (busy) replaces the leading icon; label; trailing icon.
  const inner = (
    <>
      {isBusy ? (
        <span className="csb__spinner" aria-hidden="true" />
      ) : iconLeading != null ? (
        <span className="csb__icon">{iconLeading}</span>
      ) : null}
      {visibleLabel != null ? <span className="csb__label">{visibleLabel}</span> : null}
      {!isBusy && iconTrailing != null ? <span className="csb__icon">{iconTrailing}</span> : null}
    </>
  );

  // Description composes: caller ids first, then our error id while it shows.
  // Never `undefined`-overwrite a caller value (that is the CR-04 defect).
  const describedBy =
    [rest['aria-describedby'], showError ? errorId : null].filter(Boolean).join(' ') || undefined;

  // Common a11y wiring for both elements. Keys are only SET when they carry a
  // value, so spreading these after `...rest` can never erase a caller attribute
  // with `undefined` (Field.jsx uses the same shape).
  const sharedProps = {
    className: btnClass,
    'data-component': 'Button',
    'data-variant': resolvedVariant,
    'data-size': resolvedSize,
    'data-state': dataState,
    onClick: handleClick,
  };
  if (isBusy) sharedProps['aria-busy'] = true;
  if (describedBy) sharedProps['aria-describedby'] = describedBy;
  // Last-resort accessible name: if the control has no visible label and the
  // host supplied no explicit name (aria-label/-labelledby/title), fall back to
  // a generic one so the rendered button is never nameless (WCAG 4.1.2 / axe
  // button-name). The dev-warning above still nudges hosts to pass a real
  // label; when they do, `missingName` is false and their name rides in via
  // `...rest` untouched.
  if (missingName) sharedProps['aria-label'] = 'Button';

  let control;
  if (isLink) {
    const safeHref = (h) => { if (typeof h !== "string") return undefined; const v = h.trim().toLowerCase(); return /^(javascript|vbscript|data):/.test(v.replace(/[\s\u0000-\u001f]+/g, "")) ? undefined : h; };
    // External-target safety: always enforce a hardened rel for `_blank`,
    // merging `noopener noreferrer` into any caller-provided rel.
    const computedRel =
      target === '_blank'
        ? [rel, 'noopener', 'noreferrer']
            .filter(Boolean)
            .join(' ')
            .split(/\s+/)
            .filter((v, i, a) => v && a.indexOf(v) === i)
            .join(' ')
        : rel != null
        ? rel
        : undefined;
    control = (
      <a
        {...safeRest(rest)}
        {...safeRest(sharedProps)}
        ref={setRef}
        href={isInert ? undefined : safeHref(href)}
        target={target}
        rel={computedRel}
        role={isInert ? 'link' : undefined}
        aria-disabled={isInert || undefined}
        tabIndex={isInert ? -1 : rest.tabIndex}
      >
        {inner}
      </a>
    );
  } else {
    control = (
      <button {...safeRest(rest)} {...safeRest(sharedProps)} ref={setRef} type={type} disabled={isInert}>
        {inner}
      </button>
    );
  }

  return (
    <span className={wrapperClass}>
      {control}

      {/*
        Polite busy cue. Always rendered (empty when idle) so insertions are
        announced reliably — via a live region rather than inside the disabled
        control, where AT may skip it. Suppressed when a visible `loadingLabel`
        is supplied (that text already conveys the change).
      */}
      <span className="csb__sr" aria-live="polite">
        {isBusy && loadingLabel == null ? busyLabel : ''}
      </span>

      {showError ? (
        // A <span> (not <p>): the wrapper is phrasing content, so a <Button>
        // stays valid even inline within a sentence — a block <p> here would be
        // hoisted out of the span by the parser.
        //
        // One announcement channel, not two: this node is the control's
        // `aria-describedby` target (read when the control gets focus) AND a
        // *polite* live region (announced once when it appears). It is NOT a
        // role="alert" — assertive interruption fights with the describedby
        // read and contradicts the calm, never-panics error posture (§2.7).
        // loading and error are mutually exclusive, so this never overlaps the
        // busy cue in the sibling live region.
        <span className="csb__error" id={errorId} aria-live="polite" aria-atomic="true">
          <ErrorGlyph />
          <span>{errorMessage}</span>
        </span>
      ) : null}
    </span>
  );
}

export default Button;
